import { useAccountInfo } from '@/hooks/query/account';
import { cookieService } from '@/services/cookie.service';
import { useAuthToken } from '@/store/auth';
import { useSiteStore } from '@/store/site';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { MD5 } from '@/utils/cfsEncodeFunc';
import { RSACrypt } from '@/utils/rsa-provider';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { Box, Maybe, MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { useState } from 'react';

const initialErrorMessage = {
	gestureCode: '',
}

export enum GestureSettingSteps {
	Set = 0,
	Confirm = 1,
}

export const useGestureSetting = () => {
	const [isSuccess, setIsSuccess] = useState(false);
	const [step, setStep] = useState<GestureSettingSteps>(0);
	const [preGestureCode, setPreGestureCode] = useState('');
	const { user } = useAccountInfo();
	const apiDomain = useSiteStore((state) => state.apiDomain);
	const authToken = useAuthToken();
	const [errorMessage, setErrorMessage] = useState(initialErrorMessage);

	function initGestureSetting () {
		setStep(0);
		setPreGestureCode('');
		initErrorMessage();
		setIsSuccess(false);
	}

	function initErrorMessage() {
		setErrorMessage(initialErrorMessage);
	}

	async function getPublicKey (): Promise<{
		EKEY?: string,
		MKEY?: string,
	}> {
		try {
			const  response = await fetchComposer.post(cloudUrlParser(`${apiDomain.ctcdDomain}/Default/GetPublicKey`),  {});
			const  data = await response.json();
			if (data.ErrorCode == 0) {
				return {
					EKEY: data?.Data?.EKEY,
					MKEY: data?.Data?.MKEY,
				};
			} else {
				throw new Error('getPublicKey Error');
			}
		} catch (e) {
			throw e
		}
	}

	function handleCookieSetting () {
		MaybeMatch(cookieService.getRememberMe())
			.fold(
				() => cookieService.setCookie('rememberMe', 'true', 365),
				() => {}
			)
		Maybe(user?.LoginUserName)
			.fold(
				() => {},
				(loginUserName: string) => {
					cookieService.addGestureUser(loginUserName);
					cookieService.addUserNames(loginUserName);
				}
			)

	}
	
	async function saveGesture(gestureCode: string): Promise<boolean> {
		try {
			const { EKEY, MKEY } = await getPublicKey();
			if (!EKEY || !MKEY) {
				throw new Error('Failed to retrieve public keys.');
			}
			const provider = new RSACrypt(EKEY, MKEY, 1024);
			const encryptedCode = Box(gestureCode)
				.map(MD5)
				.map((str: string) => provider.encryptedString(str))
				.extract();
	
			if (!authToken) {
				throw new Error('Authentication token not found.');
			}
			const formData = new URLSearchParams();
			formData.append('Gesture', encryptedCode);
			const response = await fetchComposer.postWithBearer(
				cloudUrlParser(`${apiDomain.ctcdDomain}/setting/SaveGesture`),
				{
					body: formData.toString(),
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
						Accept: 'application/json',
					},
				}
			)(authToken);
			const data = await response.json();
			if (data?.Data?.Code === 1) {
				handleCookieSetting();
				return true;
			} else {
				throw new Error('Failed to save gesture.');
			}
		} catch (error) {
			throw error;
		}
	}
	
	async function settingGestureCode(selectedPattern: number[]): Promise<boolean> {
		try {
			if (selectedPattern.length < 4) {
				throw new PatternError('Pattern must have at least 4 points.');
			}
			const isSaveSuccessful = await saveGesture(selectedPattern.join(''));
			setIsSuccess(true);
			return isSaveSuccessful;
		} catch (error) {
			if (error instanceof PatternError) {
				setErrorMessage((pre) => ({ ...pre, gestureCode: 'lbl_LockRem_4Point' }));
			} else {
				setErrorMessage((pre) => ({ ...pre, gestureCode: 'lbl_RPError02' }));
			}
			return false;
		}
	}


	async function checkIsPatternValid(selectedPattern: number[]) {
		initErrorMessage();

		if (GestureSettingSteps.Set === step && selectedPattern.length >= 4) {
			setPreGestureCode(selectedPattern.join(''));
			setStep(GestureSettingSteps.Confirm);
			return true;
		} 
  
		if (GestureSettingSteps.Confirm === step) {
			if (preGestureCode === selectedPattern.join('') && selectedPattern.length >= 4) {
				const isSuccess = await settingGestureCode(selectedPattern);
				return isSuccess;
			} else {
				setStep(GestureSettingSteps.Set);
				setErrorMessage((prev) => ({
					...prev,
					gestureCode: 'lbl_LockRem_WrongPWD',
				}));
				return false;
			}
		}

		setStep(GestureSettingSteps.Set);
		setErrorMessage((prev) => ({
			...prev,
			gestureCode: 'lbl_LockRem_4Point',
		}));
  
		return false;
	}
	
	return {
		step,
		isSuccess,
		errorMessage,
		checkIsPatternValid,
		initGestureSetting,
	}
}

class PatternError extends Error {}